import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/axios';
import Header from '../components/Header';
import BrunoMessage from '../components/BrunoMessage';
import estadosBrasil from '../data/estadosBrasil';
import { TIPOS_CONSTRUCAO, PADROES_CONSTRUCAO } from '../data/etapasObra';

const OrcamentoValidacaoNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form state
  const [areaTotalValidacao, setAreaTotalValidacao] = useState('');
  const [tipoConstrucao, setTipoConstrucao] = useState('');
  const [padrao, setPadrao] = useState('');
  const [estado, setEstado] = useState('');
  const [comodos, setComodos] = useState([]);
  const [itensEspeciais, setItensEspeciais] = useState([]);
  const [observacoes, setObservacoes] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [calculando, setCalculando] = useState(false);
  const [erro, setErro] = useState(null);

  // Itens especiais with prices (unificados com CUB_EXTRAS)
  const ITENS_COM_PRECO = [
    { id: 'churrasqueira', nome: 'Churrasqueira', preco: 5000 },
    { id: 'piscina', nome: 'Piscina', preco: 65000 },
    { id: 'muro_portao', nome: 'Muro/Portão', preco: 35000 },
    { id: 'cisterna', nome: 'Cisterna', preco: 4000 },
    { id: 'energia_solar', nome: 'Energia Solar', preco: 18000 },
    { id: 'paisagismo', nome: 'Paisagismo', preco: 25000 },
    { id: 'ar_condicionado', nome: 'Ar Condicionado', preco: 6000 },
    { id: 'automacao', nome: 'Automação', preco: 12000 },
    { id: 'banheira', nome: 'Banheira', preco: 8000 },
    { id: 'lareira', nome: 'Lareira', preco: 5000 },
  ];

  // Load analysis and orcamento data on mount
  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);

        // Get analysis from localStorage
        const analiseStored = localStorage.getItem(`orcamento_analise_${id}`);
        const analise = analiseStored ? JSON.parse(analiseStored) : null;

        // Fetch orcamento from API
        const response = await api.get(`/api/orcamento/${id}`);
        const orcamento = response.data;

        // Pre-populate form with analysis data
        if (analise) {
          setAreaTotalValidacao(analise.area_total || '');
          setTipoConstrucao(analise.tipo_construcao || '');
          setPadrao(analise.padrao || '');
          setEstado(analise.estado || '');
          setComodos(analise.comodos || []);
          setItensEspeciais(analise.itens_especiais || []);
          setObservacoes(analise.observacoes || []);
        } else if (orcamento) {
          // Fallback to orcamento data if analysis not available
          setAreaTotalValidacao(orcamento.area_total || '');
          setTipoConstrucao(orcamento.tipo_construcao || '');
          setPadrao(orcamento.padrao || '');
          setEstado(orcamento.estado || '');
          setComodos(orcamento.comodos || []);
          setItensEspeciais(orcamento.itens_especiais || []);
        }

        setLoading(false);
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        setErro('Erro ao carregar dados da análise');
        setLoading(false);
      }
    };

    carregarDados();
  }, [id]);

  // Handle comodo changes
  const handleComodoChange = (index, field, value) => {
    const novoComodos = [...comodos];
    novoComodos[index] = {
      ...novoComodos[index],
      [field]: field === 'quantidade' || field === 'area' ? parseFloat(value) || 0 : value,
    };
    setComodos(novoComodos);
  };

  // Delete comodo
  const deletarComodo = (index) => {
    setComodos(comodos.filter((_, i) => i !== index));
  };

  // Add new comodo
  const adicionarComodo = () => {
    setComodos([
      ...comodos,
      { nome: '', quantidade: 1, area: 0 },
    ]);
  };

  // Toggle item especial
  const toggleItemEspecial = (itemId) => {
    if (itensEspeciais.includes(itemId)) {
      setItensEspeciais(itensEspeciais.filter(id => id !== itemId));
    } else {
      setItensEspeciais([...itensEspeciais, itemId]);
    }
  };

  // Calculate total area from comodos
  const calcularAreaTotal = () => {
    return comodos.reduce((total, comodo) => total + (comodo.area || 0), 0);
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!areaTotalValidacao || !tipoConstrucao || !padrao || !estado) {
      setErro('Por favor, preencha todos os campos gerais');
      return;
    }

    if (comodos.length === 0) {
      setErro('Por favor, adicione pelo menos um cômodo');
      return;
    }

    try {
      setCalculando(true);
      setErro(null);

      // Validar dados com API
      await api.put(`/api/orcamento/${id}`, {
        area_total: parseFloat(areaTotalValidacao),
        comodos: comodos.map(c => ({
          nome: c.nome,
          quantidade: c.quantidade || 1,
          area: c.area || 0,
        })),
        itens_especiais: itensEspeciais,
        tipo: tipoConstrucao,
        padrao: padrao,
        estado: estado,
      });

      // Calcular orçamento
      const resultResponse = await api.post(`/api/orcamento/${id}`);

      // Store result in localStorage
      localStorage.setItem(`orcamento_resultado_${id}`, JSON.stringify(resultResponse.data));

      // Navigate to resultado page
      navigate(`/orcamento/${id}/resultado`);
    } catch (err) {
      console.error('Erro ao calcular orçamento:', err);
      setErro(err.response?.data?.message || 'Erro ao calcular orçamento');
      setCalculando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-center">
            <p className="text-gray-600">Carregando análise...</p>
          </div>
        </div>
      </div>
    );
  }

  const areaComodos = calcularAreaTotal();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-12 max-w-5xl">
        {/* Título */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-navy-900">Validar Análise do Bruno</h1>
          <p className="text-gray-600 mt-2">Revise e edite a análise feita pela IA antes de calcular o orçamento</p>
        </div>

        {/* Erro */}
        {erro && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
            <p className="text-red-700 text-sm">{erro}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* SECTION 1 - Dados Gerais */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-navy-900 mb-6">Dados Gerais</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Área Total */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-navy-900 mb-2">Área Total (m²)</label>
                <input
                  type="number"
                  step="0.01"
                  value={areaTotalValidacao}
                  onChange={(e) => setAreaTotalValidacao(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  required
                />
              </div>

              {/* Tipo de Construção */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-navy-900 mb-2">Tipo de Construção</label>
                <select
                  value={tipoConstrucao}
                  onChange={(e) => setTipoConstrucao(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  required
                >
                  <option value="">Selecione...</option>
                  {TIPOS_CONSTRUCAO.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {tipo}
                    </option>
                  ))}
                </select>
              </div>

              {/* Padrão */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-navy-900 mb-2">Padrão</label>
                <select
                  value={padrao}
                  onChange={(e) => setPadrao(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  required
                >
                  <option value="">Selecione...</option>
                  {PADROES_CONSTRUCAO.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              {/* Estado */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-navy-900 mb-2">Estado</label>
                <select
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  required
                >
                  <option value="">Selecione...</option>
                  {estadosBrasil.map((est) => (
                    <option key={est.sigla} value={est.sigla}>
                      {est.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 2 - Cômodos */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-navy-900">Cômodos Identificados</h2>
                <p className="text-sm text-gray-600 mt-1">Área total identificada: {areaComodos.toFixed(2)} m²</p>
              </div>
            </div>

            {/* Tabela de cômodos */}
            <div className="overflow-x-auto mb-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-navy-900">Nome</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-navy-900">Quantidade</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-navy-900">Área (m²)</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-navy-900">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {comodos.map((comodo, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={comodo.nome || ''}
                          onChange={(e) => handleComodoChange(index, 'nome', e.target.value)}
                          placeholder="Ex: Sala"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                          required
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="1"
                          value={comodo.quantidade || 1}
                          onChange={(e) => handleComodoChange(index, 'quantidade', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={comodo.area || 0}
                          onChange={(e) => handleComodoChange(index, 'area', e.target.value)}
                          placeholder="0.00"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => deletarComodo(index)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition"
                        >
                          <span className="text-lg">✕</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Adicionar cômodo */}
            <button
              type="button"
              onClick={adicionarComodo}
              className="w-full py-2 rounded-lg border border-dashed border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
            >
              + Adicionar Cômodo
            </button>
          </div>

          {/* SECTION 3 - Itens Especiais */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-navy-900 mb-6">Itens Especiais</h2>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {ITENS_COM_PRECO.map((item) => (
                <label
                  key={item.id}
                  className="flex items-start p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition"
                >
                  <input
                    type="checkbox"
                    checked={itensEspeciais.includes(item.id)}
                    onChange={() => toggleItemEspecial(item.id)}
                    className="mt-1 rounded border-gray-300"
                  />
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-navy-900">{item.nome}</p>
                    <p className="text-xs text-gray-600">R$ {item.preco.toLocaleString('pt-BR')}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* SECTION 4 - Observações do Bruno */}
          {observacoes && observacoes.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <BrunoMessage pose="tablet" type="tip">
                <div className="space-y-2">
                  <h3 className="font-semibold text-navy-900">Observações da Análise:</h3>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {observacoes.map((obs, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{obs}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </BrunoMessage>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6">
            <Link
              to={`/orcamento/${id}`}
              className="px-6 py-3 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 transition"
            >
              Voltar
            </Link>
            <button
              type="submit"
              disabled={calculando}
              className="flex-1 px-6 py-3 rounded-lg bg-brand-orange text-white font-bold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {calculando ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Calculando...
                </>
              ) : (
                <>
                  Calcular Orçamento
                  <span>→</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrcamentoValidacaoNew;
